/**
 * 学习乐园 · 孔明棋三角版（game-kongming.js）
 * ============================================================
 * 「逻辑与推理」组新作：入门 15 孔三角（默认档，可解空位开局）、进阶 15 孔
 * （双空开局）、挑战 15 孔（开局空哪，最后剩的球就要落在哪——「归位」）。
 * 规则：球跳过相邻球落到直线另一侧的空孔，被跳的球拿走。
 *
 * 出题/判定底线：开局空位从「验证可解到剩 1 球」的集合里挑（记忆化 DFS
 * 预验证），绝不发死题。查一查从当前局面现算：还能不能剩 1 球、下一步
 * 怎么跳（给「跳完这步仍有解」的着法），是真实搜索不是口诀。
 *
 * 反馈口径：点空孔讲「先选中一颗球」；跳不了讲具体原因；走进死局当场讲
 * 「剩 N 颗球没有可跳的组合」——撤销随时可用。剩 1 球即胜（挑战档还要
 * 落在开局空位）。不计时，成绩按档位记完成局数，存本机 localStorage。
 *
 * 冒烟钩子：window.STKongming 暴露 rowOf / jumpsOf / canSolve / bestMove
 * 与 state()（{holes, sel, mode, goal, ended}）。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-kongming';
  var RECORD_KEY = 'study-kongming-record';

  /* ============ 引擎：纯函数（三角 15 孔，idx = r(r+1)/2 + c） ============ */

  function idx(r, c) { return r * (r + 1) / 2 + c; }

  function rowOf(i) {
    var r = 0;
    while (r * (r + 1) / 2 <= i) r++;
    return r - 1;
  }

  function colOf(i) {
    var r = rowOf(i);
    return i - r * (r + 1) / 2;
  }

  // 六方向（dr,dc）：行内左右；下邻是 (r+1,c) 与 (r+1,c+1)，上邻反向
  var DIRS = [[0, -1], [0, 1], [1, 0], [1, 1], [-1, 0], [-1, -1]];

  function inside(r, c) { return r >= 0 && r < 5 && c >= 0 && c <= r; }

  // 从 src 出发能跳的着法 [{src, mid, dst}]
  function jumpsOf(holes, src) {
    var out = [];
    var r = rowOf(src), c = colOf(src);
    for (var d = 0; d < DIRS.length; d++) {
      var mr = r + DIRS[d][0], mc = c + DIRS[d][1];
      var er = r + 2 * DIRS[d][0], ec = c + 2 * DIRS[d][1];
      if (!inside(mr, mc) || !inside(er, ec)) continue;
      var mid = idx(mr, mc), dst = idx(er, ec);
      if (holes[src] && holes[mid] && !holes[dst]) out.push({ src: src, mid: mid, dst: dst });
    }
    return out;
  }

  function allJumps(holes) {
    var out = [];
    for (var i = 0; i < 15; i++) {
      var js = jumpsOf(holes, i);
      for (var k = 0; k < js.length; k++) out.push(js[k]);
    }
    return out;
  }

  function applyJump(holes, mv) {
    var next = holes.slice();
    next[mv.src] = 0;
    next[mv.mid] = 0;
    next[mv.dst] = 1;
    return next;
  }

  // 记忆化搜索：holes 局面能否走到剩 1 球（goal: 挑战档要求剩球位置，-1 不限）
  // 返回 {ok, move} —— move 是「走完这步仍有解」的着法
  var memo = null;
  function search(holes, goal) {
    memo = {};
    return searchGo(holes, goal);
  }

  function searchGo(holes, goal) {
    var key = holes.join('') + '|' + goal;
    if (memo[key] !== undefined) return memo[key];
    var count = 0;
    for (var i = 0; i < 15; i++) count += holes[i];
    if (count === 1) {
      var res = { ok: goal < 0 || holes[goal] === 1, move: null };
      memo[key] = res;
      return res;
    }
    var jumps = allJumps(holes);
    if (!jumps.length) {
      var dead = { ok: false, move: null };
      memo[key] = dead;
      return dead;
    }
    for (var k = 0; k < jumps.length; k++) {
      var after = searchGo(applyJump(holes, jumps[k]), goal);
      if (after.ok) {
        var good = { ok: true, move: jumps[k] };
        memo[key] = good;
        return good;
      }
    }
    var fail = { ok: false, move: null };
    memo[key] = fail;
    return fail;
  }

  function canSolve(holes, goal) { return search(holes, goal).ok; }
  function bestMove(holes, goal) { return search(holes, goal).move; }

  // 每个开局空位的可解性（入门/进阶用；挑战档也要求归位可解）
  var SOLVABLE_STARTS = null;
  function solvableStarts() {
    if (SOLVABLE_STARTS) return SOLVABLE_STARTS;
    var full = new Array(15).fill(1);
    var ok = [];
    for (var e = 0; e < 15; e++) {
      var h = full.slice();
      h[e] = 0;
      if (canSolve(h, -1)) ok.push(e);
    }
    SOLVABLE_STARTS = ok;
    return ok;
  }

  var SOLVABLE_GOALS = null;
  function solvableGoals() {
    if (SOLVABLE_GOALS) return SOLVABLE_GOALS;
    var full = new Array(15).fill(1);
    var ok = [];
    for (var e = 0; e < 15; e++) {
      var h = full.slice();
      h[e] = 0;
      if (canSolve(h, e)) ok.push(e);
    }
    SOLVABLE_GOALS = ok;
    return ok;
  }

  /* ============ UI ============ */

  var LEVELS = [
    { mode: 'single', name: '入门 15 孔', hint: '空 1 孔 · 默认档' },
    { mode: 'double', name: '进阶 15 孔', hint: '空 2 孔' },
    { mode: 'home', name: '挑战 · 归位', hint: '剩球要回空位' }
  ];

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var root = null;
  var ui = {};
  var S = {
    mode: 'single', holes: [], sel: -1, goal: -1, start: -1,
    ended: false, soundOn: true, resetArm: false, resetTimer: null
  };

  function renderLevels() {
    var box = ui.levels;
    box.textContent = '';
    for (var i = 0; i < LEVELS.length; i++) {
      (function (lv, di) {
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-level', String(i));
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', lv.name));
        b.appendChild(el('small', 'st-llk-pack-hint', lv.hint));
        b.addEventListener('click', function () {
          if (S.mode === lv.mode) return;
          newGame(di);
        });
        box.appendChild(b);
      })(LEVELS[i], i);
    }
  }

  function syncLevels() {
    var btns = ui.levels.querySelectorAll('[data-level]');
    for (var i = 0; i < btns.length; i++) {
      var on = Number(btns[i].getAttribute('data-level')) === LEVELS.map(function (l) { return l.mode; }).indexOf(S.mode);
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function buildBoard() {
    var board = ui.board;
    board.textContent = '';
    for (var r = 0; r < 5; r++) {
      var row = el('div', 'st-km-row');
      for (var c = 0; c <= r; c++) {
        (function (i) {
          var b = el('button', 'st-km-hole');
          b.type = 'button';
          b.setAttribute('data-idx', String(i));
          b.addEventListener('click', function () { onHole(i); });
          row.appendChild(b);
        })(idx(r, c));
      }
      board.appendChild(row);
    }
    renderBoard();
  }

  function renderBoard() {
    var holes = ui.board.children;
    for (var r = 0; r < holes.length; r++) {
      var cells = holes[r].children;
      for (var c = 0; c < cells.length; c++) {
        var i = idx(r, c);
        var node = cells[c];
        node.classList.toggle('is-full', S.holes[i] === 1);
        node.classList.toggle('is-sel', S.sel === i);
        node.classList.toggle('is-goal', S.goal === i);
        node.classList.toggle('is-hint', node._hintDst === i || node._hintSrc === i || node._hintMid === i);
        node.setAttribute('aria-label', (r + 1) + ' 行 ' + (c + 1) + ' 列：' + (S.holes[i] ? '有球' : '空孔'));
      }
    }
    renderStatus();
  }

  function renderStatus() {
    if (S.ended) return;
    var count = 0;
    for (var i = 0; i < 15; i++) count += S.holes[i];
    var lvName = '';
    for (var k = 0; k < LEVELS.length; k++) { if (LEVELS[k].mode === S.mode) lvName = LEVELS[k].name; }
    ui.status.className = 'st-status';
    ui.status.textContent = lvName + ' · 剩 ' + count + ' 颗球'
      + (S.mode === 'home' ? ' · 剩球要落在 ' + (S.goal + 1) + ' 号孔（金圈）' : '');
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    for (var i = 0; i < LEVELS.length; i++) {
      var r = rec['x' + i];
      box.appendChild(recordStat(
        LEVELS[i].name + '完成',
        (r && r.wins ? r.wins + ' 局' : '还没解到剩 1 颗球')
      ));
    }
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'win') seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];
      else if (kind === 'bad') seq = [[196, 0], [185, 0.09]];
      else seq = [[330, 0]];
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g); g.connect(actx.destination);
        o.start(t + seq[i][1]); o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  function clearHints() {
    var cells = ui.board.querySelectorAll('.st-km-hole');
    for (var i = 0; i < cells.length; i++) { cells[i]._hintDst = cells[i]._hintSrc = cells[i]._hintMid = -1; }
  }

  function markHint(mv) {
    clearHints();
    var cells = ui.board.querySelectorAll('.st-km-hole');
    for (var i = 0; i < cells.length; i++) {
      var v = Number(cells[i].getAttribute('data-idx'));
      if (v === mv.src) cells[i]._hintSrc = v;
      if (v === mv.mid) cells[i]._hintMid = v;
      if (v === mv.dst) cells[i]._hintDst = v;
    }
  }

  function onHole(i) {
    if (S.ended) return;
    if (S.sel < 0) {
      if (!S.holes[i]) {
        renderNote('空孔跳不了——先点一颗球选中它，再点它要跳去的空孔。', '');
        return;
      }
      S.sel = i;
      sfx('sel');
      renderBoard();
      var js = jumpsOf(S.holes, i);
      renderNote('选中了 ' + (rowOf(i) + 1) + ' 行第 ' + (colOf(i) + 1) + ' 个孔的球——'
        + (js.length ? '它能跳去 ' + js.map(function (m) { return (rowOf(m.dst) + 1) + ' 行第 ' + (colOf(m.dst) + 1); }).join(' 或 ')
          + '：点那个空孔。' : '它现在四面都跳不了，换一颗球。'), '');
      return;
    }
    if (i === S.sel) {
      S.sel = -1;
      clearHints();
      renderBoard();
      renderNote('取消了选择。', '');
      return;
    }
    // 找从 sel 到 i 的跳法
    var mv = null;
    var list = jumpsOf(S.holes, S.sel);
    for (var k = 0; k < list.length; k++) { if (list[k].dst === i) mv = list[k]; }
    if (!mv) {
      sfx('bad');
      var dr = rowOf(i) - rowOf(S.sel), dc = colOf(i) - colOf(S.sel);
      var reason;
      var midR = rowOf(S.sel) + dr / 2, midC = colOf(S.sel) + dc / 2;
      var midOk = Math.abs(dr) <= 2 && Math.abs(dc) <= 2 && dr % 2 === 0 && dc % 2 === 0
        && inside(midR, midC) && S.holes[idx(midR, midC)];
      if (S.holes[i]) reason = '落点 ' + (rowOf(i) + 1) + ' 行那个孔里已经有球——要跳进空孔';
      else if (!midOk) reason = '这两孔不共线或中间隔着空孔——跳要「隔一颗球、落直线另一头」';
      else reason = '跳不成——检查方向：只能沿六个方向直线跳';
      renderNote(reason + '。换目标或换一颗球。', 'bad');
      return;
    }
    S.hist.push(S.holes);
    S.holes = applyJump(S.holes, mv);
    S.sel = -1;
    clearHints();
    sfx('ok');
    var count = 0;
    for (var q = 0; q < 15; q++) count += S.holes[q];
    if (count === 1 && (S.goal < 0 || S.holes[S.goal] === 1)) {
      renderBoard();
      winGame();
      return;
    }
    if (count === 1 && S.goal >= 0 && S.holes[S.goal] !== 1) {
      S.ended = true;
      sfx('bad');
      renderBoard();
      ui.status.className = 'st-status is-over';
      ui.status.textContent = '剩 1 颗球了，但落在 ' + (rowOf(S.holes.indexOf(1)) + 1) + ' 行——归位规则要求它落在开局空孔（金圈）。点「新开一局」再来。';
      renderNote('「归位」是孔明棋的高难度收尾：很多局面能剩 1 球，但恰好回到空位需要提前规划终点。', 'bad');
      renderButtons();
      return;
    }
    renderBoard();
    if (!allJumps(S.holes).length) {
      sfx('bad');
      renderNote('卡住了：剩 ' + count + ' 颗球，没有可跳的组合。点「撤销一步」退回去——换一步跳法。查一查可以看哪步走得对。', 'bad');
      return;
    }
    renderNote('跳掉了 ' + (rowOf(mv.mid) + 1) + ' 行那颗球——还剩 ' + count + ' 颗。', '');
  }

  function undo() {
    if (S.ended || !S.hist) return;
    if (!S.hist.length) {
      renderNote('还没走过，没法撤销。', '');
      return;
    }
    S.holes = S.hist.pop();
    S.sel = -1;
    clearHints();
    if (S.ended) { S.ended = false; ui.status.className = 'st-status'; }
    sfx('sel');
    renderBoard();
    renderNote('退回上一步。换一步跳法试试。', '');
  }

  function checkBoard() {
    if (S.ended) return;
    var count = 0;
    for (var i = 0; i < 15; i++) count += S.holes[i];
    var mv = bestMove(S.holes, S.goal);
    if (count === 1) {
      renderNote('只剩 1 颗球了——' + (S.goal < 0 || S.holes[S.goal] === 1 ? '已成局！' : '但归位要求它落在金圈孔。'), 'ok');
      return;
    }
    if (!mv) {
      renderNote('从当前局面搜索过：剩 ' + count + ' 颗球时已经无法走到剩 1 颗——点「撤销一步」退回去换跳法。', 'bad');
      return;
    }
    markHint(mv);
    renderBoard();
    renderNote('下一步这样跳：' + (rowOf(mv.src) + 1) + ' 行第 ' + (colOf(mv.src) + 1) + ' 颗球，跳过中间球，落到 '
      + (rowOf(mv.dst) + 1) + ' 行第 ' + (colOf(mv.dst) + 1) + ' 孔（盘上已亮起）。走完这步仍能剩 1 颗球。', 'ok');
  }

  function winGame() {
    S.ended = true;
    sfx('win');
    var rec = loadRecord();
    var key = 'x' + LEVELS.map(function (l) { return l.mode; }).indexOf(S.mode);
    var cur = rec[key];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) cur = { wins: 0 };
    cur.wins++;
    rec[key] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '解到了！只剩 1 颗球' + (S.goal >= 0 ? '，还恰好归位到开局空孔。' : '。') + '点「新开一局」再来。';
    renderNote('孔明棋的收尾思路：把「孤球」一路合并——尽量让球保持成对相邻，最后三颗排成直线两连跳收尾。挑战档的归位则要提前给终点留路。', 'ok');
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置孔明棋的本机成绩');
  }

  function renderButtons() {
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, mode: S.mode, soundOn: S.soundOn }));
    } catch (e) { /* 存储不可用静默 */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      if (d.mode === 'single' || d.mode === 'double' || d.mode === 'home') S.mode = d.mode;
      S.soundOn = d.soundOn !== false;
    } catch (e) { /* 忽略脏数据 */ }
  }

  function newGame(levelIdx) {
    var lv = LEVELS[levelIdx];
    S.mode = lv.mode;
    S.holes = new Array(15).fill(1);
    S.sel = -1;
    S.goal = -1;
    S.hist = [];
    S.ended = false;
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    if (lv.mode === 'single') {
      var starts = solvableStarts();
      S.start = starts[Math.floor(Math.random() * starts.length)];
      S.holes[S.start] = 0;
    } else if (lv.mode === 'double') {
      var starts2 = solvableStarts();
      var e1 = starts2[Math.floor(Math.random() * starts2.length)];
      S.holes[e1] = 0;
      // 第二个空孔：从相邻跳一步的位置挑（保证局面仍在可解集合附近），验证后用
      var e2 = -1;
      var order = [];
      for (var z = 0; z < 15; z++) order.push(z);
      for (var z2 = order.length - 1; z2 > 0; z2--) {
        var j = Math.floor(Math.random() * (z2 + 1));
        var t = order[z2]; order[z2] = order[j]; order[j] = t;
      }
      for (var q = 0; q < order.length; q++) {
        if (S.holes[order[q]] === 1) { S.holes[order[q]] = 0; if (canSolve(S.holes, -1)) { e2 = order[q]; break; } S.holes[order[q]] = 1; }
      }
      S.start = e1;   // e2 找不到时局面保持单空（仍是可解的合法开局）
    } else {
      var goals = solvableGoals();
      S.goal = goals[Math.floor(Math.random() * goals.length)];
      S.start = S.goal;
      S.holes[S.goal] = 0;
    }
    syncLevels();
    buildBoard();
    renderRecord();
    renderButtons();
    var lvName = LEVELS[levelIdx].name;
    renderNote('「' + lvName + '」：点球选中，再点它能跳到的空孔——被跳过的球拿走。'
      + (S.mode === 'home' ? '最后剩的 1 颗球必须落在开局空孔（金圈）。' : '解到只剩 1 颗球就赢。'), '');
    save();
  }

  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.levels = root.querySelector('[data-st-km-levels]');
    ui.board = root.querySelector('[data-st-km-board]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.record = root.querySelector('[data-st-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.checkBtn = root.querySelector('[data-st-check]');
    ui.undoBtn = root.querySelector('[data-st-km-undo]');
    ui.resetBtn = root.querySelector('[data-st-reset]');

    restorePrefs();
    renderLevels();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(LEVELS.map(function (l) { return l.mode; }).indexOf(S.mode)); });
    ui.checkBtn.addEventListener('click', checkBoard);
    ui.undoBtn.addEventListener('click', undo);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame(LEVELS.map(function (l) { return l.mode; }).indexOf(S.mode));
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STKongming = {
    rowOf: rowOf,
    colOf: colOf,
    idx: function (r, c) { return r * (r + 1) / 2 + c; },
    jumpsOf: jumpsOf,
    allJumps: allJumps,
    applyJump: applyJump,
    canSolve: canSolve,
    bestMove: bestMove,
    solvableStarts: solvableStarts,
    solvableGoals: solvableGoals,
    state: function () { return { holes: S.holes, sel: S.sel, mode: S.mode, goal: S.goal, ended: S.ended }; }
  };
})(typeof window !== 'undefined' ? window : globalThis);
